/**
 * PostgreSQL v2 API Utility
 * 
 * This utility is for components that have been migrated to use PostgreSQL v2 endpoints.
 * Use this instead of the regular api utility for migrated components.
 * 
 * All calls automatically:
 * - Add JWT token from localStorage
 * - Use v2 endpoint prefix (/api/v2/...)
 * - Include organization context
 */
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../config/api';
import { isTokenExpired, refreshSession } from './sessionRefresh';
import { attachOfflineCache } from './offlineCache';
import { attachOfflineQueue } from './offlineQueue';

// Create axios instance with v2 base URL
const apiV2 = axios.create({
  baseURL: `${API_URL}/v2`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add JWT token and org context
apiV2.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const token = localStorage.getItem('jwtToken');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add organization ID for org-scoped requests
    const orgId = localStorage.getItem('organizationId');
    if (orgId) {
      config.headers['X-Organization-Id'] = orgId;
    }
    
    // Add organization slug header as fallback
    const orgSlug = localStorage.getItem('organizationSlug');
    if (orgSlug) {
      config.headers['X-Organization-Slug'] = orgSlug;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
apiV2.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Handle 403 Forbidden - Permission denied
    if (error.response && error.response.status === 403) {
      // Check if this is a background data fetch (GET request to a list endpoint)
      const isBackgroundFetch = error.config?.method?.toLowerCase() === 'get' && 
        (error.config?.url?.includes('/bookings') || 
         error.config?.url?.includes('/team-members') ||
         error.config?.url?.includes('/assets') ||
         error.config?.url?.includes('/services') ||
         error.config?.url?.includes('/roles'));
      
      // Only show toast for user-initiated actions, not background fetches
      if (!isBackgroundFetch) {
        const message = error.response.data?.detail || "Your role doesn't have permission for this action";
        toast.error(message, {
          duration: 4000,
          icon: '🔒'
        });
      }
    }
    
    // Handle 401 Unauthorized
    if (error.response && error.response.status === 401) {
      const requestUrl = error.config?.url || '';
      const currentPath = window.location.pathname;
      
      // Routes that should NOT auto-redirect to login on 401
      const skipRedirectRoutes = [
        '/public/',
        '/customer-ticket',
        '/ticket/',
        '/check-in/',
        '/self-check-in',
        '/book',
        '/booking',
        '/widget',
        '/account/'  // Account management routes should not auto-logout
      ];
      
      const shouldSkipRedirect = skipRedirectRoutes.some(route => 
        requestUrl.includes(route) || currentPath.includes(route)
      );
      
      if (!shouldSkipRedirect) {
        // Try one silent token refresh before logging the user out — only
        // hard-logout when the session is genuinely expired/invalid.
        const alreadyRetried = error.config?._authRetried;
        const isRefreshCall = requestUrl.includes('/auth/refresh');
        if (!alreadyRetried && !isRefreshCall && !isTokenExpired()) {
          const refreshed = await refreshSession();
          if (refreshed) {
            error.config._authRetried = true;
            error.config.headers = error.config.headers || {};
            error.config.headers.Authorization = `Bearer ${localStorage.getItem('jwtToken')}`;
            return apiV2.request(error.config);
          }
          if (refreshed === null) {
            // Network hiccup — keep the session, let the caller handle the error
            return Promise.reject(error);
          }
        }
        localStorage.removeItem('jwtToken');
        localStorage.removeItem('userType');
        localStorage.removeItem('userData');
        localStorage.removeItem('organizationId');
        localStorage.removeItem('organizationSlug');
        
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// Offline read cache — serves manifest/schedule/crew data from IndexedDB when
// the device has no signal (see utils/offlineCache.js). Must be attached AFTER
// the auth interceptor so it sees the final rejection.
attachOfflineCache(apiV2);
// Offline action queue — check-ins made with no signal are queued and replayed
// automatically when connectivity returns (see utils/offlineQueue.js).
attachOfflineQueue(apiV2);

export default apiV2;

// Export convenience methods with org context
export const getOrgId = () => localStorage.getItem('organizationId');

// Organization-scoped convenience methods
export const apiV2Get = (url, config) => apiV2.get(url, config);
export const apiV2Post = (url, data, config) => apiV2.post(url, data, config);
export const apiV2Put = (url, data, config) => apiV2.put(url, data, config);
export const apiV2Patch = (url, data, config) => apiV2.patch(url, data, config);
export const apiV2Delete = (url, config) => apiV2.delete(url, config);

// Org-specific endpoints
export const orgApi = {
  // Organization info - uses auth endpoint for current org
  getOrganization: () => apiV2.get('/auth/organizations/me'),

  // GetMyBoat integration — status panel (Home refresh dropdown) + Sync now
  getGmbStatus: () => apiV2.get('/integrations/gmb/status'),
  gmbSyncNow: () => apiV2.post('/integrations/gmb/sync', {}),
  // AI boat-assignment suggestions for unassigned bookings (Home tab)
  suggestBoats: () => apiV2.post('/integrations/boat-suggestions', {}),
  suggestBoatForBooking: (id) => apiV2.post(`/integrations/bookings/${id}/suggest-boat`, {}),
  setOriginalUnavailable: (id, unavailable = true) =>
    apiV2.post(`/integrations/bookings/${id}/original-unavailable`, { unavailable }).then(res => res.data),
  // Dismiss a GetMyBoat "needs a look-see" flag (cancel | update) after reconciling
  dismissGmbFlag: (id, flag) =>
    apiV2.post(`/integrations/bookings/${id}/dismiss-gmb-flag`, { flag }).then(res => res.data),
  // GMB Reconcile — catch HOST-initiated cancellations (GetMyBoat sends no email for host cancels)
  getGmbReconcile: () => apiV2.get('/integrations/gmb/reconcile').then(res => res.data),
  gmbReconcile: (id, action) =>
    apiV2.post(`/integrations/bookings/${id}/gmb-reconcile`, { action }).then(res => res.data),
  getBoatSuggestSettings: () => apiV2.get('/integrations/boat-suggest/settings'),
  updateBoatSuggestSettings: (body) => apiV2.patch('/integrations/boat-suggest/settings', body),
  getBoatRecap: (since) => apiV2.get('/integrations/boat-suggest/recap', { params: since ? { since } : {} }),
  updateOrganization: (data) => apiV2.patch('/auth/organizations/me', data),
  
  // 📦 Packages (bundles of services, one payment)
  getPackages: () => apiV2.get(`/organizations/${getOrgId()}/packages`),
  createPackage: (data) => apiV2.post(`/organizations/${getOrgId()}/packages`, data),
  updatePackage: (id, data) => apiV2.patch(`/organizations/${getOrgId()}/packages/${id}`, data),
  deletePackage: (id) => apiV2.delete(`/organizations/${getOrgId()}/packages/${id}`),

  // 🏛️ Members Arena (subscribers) + Event Calendar
  getMembers: () => apiV2.get(`/organizations/${getOrgId()}/members`),
  getAnnouncements: () => apiV2.get(`/organizations/${getOrgId()}/announcements`),
  createAnnouncement: (data) => apiV2.post(`/organizations/${getOrgId()}/announcements`, data),
  updateAnnouncement: (id, data) => apiV2.patch(`/organizations/${getOrgId()}/announcements/${id}`, data),
  deleteAnnouncement: (id) => apiV2.delete(`/organizations/${getOrgId()}/announcements/${id}`),
  getClubEvents: () => apiV2.get(`/organizations/${getOrgId()}/events`),
  createClubEvent: (data) => apiV2.post(`/organizations/${getOrgId()}/events`, data),
  updateClubEvent: (id, data) => apiV2.patch(`/organizations/${getOrgId()}/events/${id}`, data),
  deleteClubEvent: (id) => apiV2.delete(`/organizations/${getOrgId()}/events/${id}`),
  getClubEventRsvps: (id) => apiV2.get(`/organizations/${getOrgId()}/events/${id}/rsvps`),

  // Services
  getServices: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.activeOnly !== undefined) queryParams.append('activeOnly', params.activeOnly);
    if (params.includeAssets) queryParams.append('includeAssets', params.includeAssets);
    const queryString = queryParams.toString();
    return apiV2.get(`/organizations/${getOrgId()}/services${queryString ? '?' + queryString : ''}`);
  },
  getService: (id) => apiV2.get(`/organizations/${getOrgId()}/services/${id}`),
  createService: (data) => apiV2.post(`/organizations/${getOrgId()}/services`, data),
  updateService: (id, data) => apiV2.patch(`/organizations/${getOrgId()}/services/${id}`, data),
  deleteService: (id) => apiV2.delete(`/organizations/${getOrgId()}/services/${id}`),
  reorderServices: (services) => apiV2.put(`/organizations/${getOrgId()}/services/reorder`, { services }),
  reactivateService: (id) => apiV2.post(`/organizations/${getOrgId()}/services/${id}/reactivate`),
  permanentDeleteService: (id) => apiV2.delete(`/organizations/${getOrgId()}/services/${id}/permanent`),
  
  // Assets
  getAssets: (activeOnly = true) => apiV2.get(`/organizations/${getOrgId()}/assets`, { params: { activeOnly } }),
  getAsset: (id) => apiV2.get(`/organizations/${getOrgId()}/assets/${id}`),
  createAsset: (data) => apiV2.post(`/organizations/${getOrgId()}/assets`, data),
  updateAsset: (id, data) => apiV2.patch(`/organizations/${getOrgId()}/assets/${id}`, data),
  deleteAsset: (id) => apiV2.delete(`/organizations/${getOrgId()}/assets/${id}`),
  
  // Team Members
  getTeamMembers: () => apiV2.get(`/organizations/${getOrgId()}/team-members`),
  getTeamMember: (id) => apiV2.get(`/organizations/${getOrgId()}/team-members/${id}`),
  createTeamMember: (data) => apiV2.post(`/organizations/${getOrgId()}/team-members`, data),
  updateTeamMember: (id, data) => apiV2.patch(`/organizations/${getOrgId()}/team-members/${id}`, data),
  deleteTeamMember: (id) => apiV2.delete(`/organizations/${getOrgId()}/team-members/${id}`),
  sendTeamMemberInvite: (id) => apiV2.post(`/organizations/${getOrgId()}/team-members/${id}/send-invite`),
  resendTeamMemberVerification: (id) => apiV2.post(`/organizations/${getOrgId()}/team-members/${id}/resend-verification`),
  
  // Bookings
  getBookings: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.status) queryParams.append('status', params.status);
    if (params.paymentFilter) queryParams.append('paymentFilter', params.paymentFilter);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);
    const queryString = queryParams.toString();
    return apiV2.get(`/organizations/${getOrgId()}/bookings${queryString ? '?' + queryString : ''}`).then(res => res.data);
  },
  getBooking: (id) => apiV2.get(`/organizations/${getOrgId()}/bookings/${id}`),
  createBooking: (data) => apiV2.post(`/organizations/${getOrgId()}/bookings`, data),
  
  // Internal booking with Stripe checkout (v2)
  createInternalBookingWithCheckout: (data) => apiV2.post(`/organizations/${getOrgId()}/internal-booking/create-with-checkout`, data),
  
  // Generic post for org endpoints
  post: (path, data) => apiV2.post(`/organizations/${getOrgId()}${path}`, data),
  updateBooking: (id, data) => apiV2.patch(`/organizations/${getOrgId()}/bookings/${id}`, data),
  sendPaymentReminder: (id, type = 'both') => apiV2.post(`/organizations/${getOrgId()}/bookings/${id}/send-payment-reminder`, { type }),
  cancelBooking: (id) => apiV2.post(`/organizations/${getOrgId()}/bookings/${id}/cancel`),
  collectBalance: (id) => apiV2.post(`/organizations/${getOrgId()}/bookings/${id}/collect-balance`),
  checkInBooking: (id) => apiV2.post(`/organizations/${getOrgId()}/bookings/${id}/check-in`),

  // Stripe Terminal — Tap to Pay (in-person balance collection, native iOS only)
  terminalConnectionToken: () => apiV2.post(`/terminal/organizations/${getOrgId()}/connection-token`),
  terminalConfig: () => apiV2.get(`/terminal/organizations/${getOrgId()}/config`),
  terminalCreateIntent: (id, payload) => apiV2.post(`/terminal/organizations/${getOrgId()}/bookings/${id}/payment-intent`, payload || {}),
  terminalSettle: (id, paymentIntentId) => apiV2.post(`/terminal/organizations/${getOrgId()}/bookings/${id}/settle`, { paymentIntentId }),

  // 📡 BookingFlows Connect — operator listing controls (affiliate APIs live in connectApi)
  connectMyListings: () => apiV2.get(`/organizations/${getOrgId()}/connect/my-listings`),
  connectToggleListing: (serviceId, optIn) => apiV2.put(`/organizations/${getOrgId()}/connect/my-listings`, { serviceId, optIn }),
  connectOrgSettings: (payload) => apiV2.put(`/organizations/${getOrgId()}/connect/settings`, payload),
  
  // AI Smart Scheduling
  getAIScheduleSuggestion: (bookingId) => apiV2.post(`/organizations/${getOrgId()}/bookings/${bookingId}/ai-schedule-suggestion`),
  getBulkScheduleSuggestions: (bookingIds) => apiV2.post(`/organizations/${getOrgId()}/bulk-schedule-suggestions`, { booking_ids: bookingIds }),
  applyBulkSchedule: (assignments) => apiV2.post(`/organizations/${getOrgId()}/bulk-apply-schedule`, { assignments }),
  
  // Calendar
  getCalendar: (startDate, endDate) => apiV2.get(`/organizations/${getOrgId()}/calendar?startDate=${startDate}&endDate=${endDate}`),
  
  // Payments
  getPaymentSummary: (startDate, endDate) => apiV2.get(`/organizations/${getOrgId()}/payments/summary?startDate=${startDate}&endDate=${endDate}`),
  getPartners: () => apiV2.get(`/organizations/${getOrgId()}/partners`),
  getPromoCodes: () => apiV2.get(`/organizations/${getOrgId()}/promo-codes`),
  
  // Customers
  getCustomers: () => apiV2.get(`/organizations/${getOrgId()}/customers`),
  getCustomer: (id) => apiV2.get(`/organizations/${getOrgId()}/customers/${id}`),
  
  // Roles
  getRoles: () => apiV2.get(`/organizations/${getOrgId()}/roles`),
  getRole: (id) => apiV2.get(`/organizations/${getOrgId()}/roles/${id}`),
  createRole: (data) => apiV2.post(`/organizations/${getOrgId()}/roles`, data),
  updateRole: (id, data) => apiV2.patch(`/organizations/${getOrgId()}/roles/${id}`, data),
  deleteRole: (id) => apiV2.delete(`/organizations/${getOrgId()}/roles/${id}`),
  
  // Financials (v2 PostgreSQL)
  getFinancialSummary: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.period) queryParams.append('period', params.period);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    const queryString = queryParams.toString();
    return apiV2.get(`/organizations/${getOrgId()}/financials/summary${queryString ? '?' + queryString : ''}`);
  },
  getFinancialBalance: () => apiV2.get(`/organizations/${getOrgId()}/financials/balance`),
  getFinancialBookings: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.period) queryParams.append('period', params.period);
    if (params.paymentMethod) queryParams.append('paymentMethod', params.paymentMethod);
    if (params.paymentStatus) queryParams.append('paymentStatus', params.paymentStatus);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    const queryString = queryParams.toString();
    return apiV2.get(`/organizations/${getOrgId()}/financials/bookings${queryString ? '?' + queryString : ''}`);
  },
  getRevenueChart: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.period) queryParams.append('period', params.period);
    if (params.groupBy) queryParams.append('groupBy', params.groupBy);
    const queryString = queryParams.toString();
    return apiV2.get(`/organizations/${getOrgId()}/financials/revenue-chart${queryString ? '?' + queryString : ''}`);
  },
  // Self-serve Trips & Money report (Financials → Reports)
  getReportTrips: (params = {}) => {
    const q = new URLSearchParams();
    if (params.dateBasis) q.append('dateBasis', params.dateBasis);
    if (params.startDate) q.append('startDate', params.startDate);
    if (params.endDate) q.append('endDate', params.endDate);
    if (params.serviceId) q.append('serviceId', params.serviceId);
    if (params.assetId) q.append('assetId', params.assetId);
    const qs = q.toString();
    return apiV2.get(`/organizations/${getOrgId()}/reports/trips${qs ? '?' + qs : ''}`).then(res => res.data);
  },
  // Trailing 12-month top-line revenue per asset (Assets tab)
  getAssetRevenue: (months = 12) =>
    apiV2.get(`/organizations/${getOrgId()}/reports/asset-revenue?months=${months}`).then((r) => r.data),
  getPayoutRequests: () => apiV2.get(`/organizations/${getOrgId()}/financials/payout-requests`),
  recordPayment: (data) => apiV2.post(`/organizations/${getOrgId()}/financials/record-payment`, data),
  
  // Expenses
  getExpenses: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.category) queryParams.append('category', params.category);
    const queryString = queryParams.toString();
    return apiV2.get(`/organizations/${getOrgId()}/expenses${queryString ? '?' + queryString : ''}`).then(res => res.data);
  },
  createExpense: (data) => apiV2.post(`/organizations/${getOrgId()}/expenses`, data).then(res => res.data),
  updateExpense: (id, data) => apiV2.put(`/organizations/${getOrgId()}/expenses/${id}`, data).then(res => res.data),
  deleteExpense: (id) => apiV2.delete(`/organizations/${getOrgId()}/expenses/${id}`).then(res => res.data),
};

// 📡 BookingFlows Connect — INDIVIDUAL affiliate APIs (not org-scoped: earnings
// are personal, so these key off the logged-in user's JWT, any role welcome)
export const connectApi = {
  me: () => apiV2.get('/connect/me'),
  updateMe: (payload) => apiV2.put('/connect/me', payload),
  catalog: (params) => apiV2.get('/connect/catalog', { params }),
  flows: () => apiV2.get('/connect/flows'),
  createFlow: (payload) => apiV2.post('/connect/flows', payload),
  updateFlow: (id, payload) => apiV2.put(`/connect/flows/${id}`, payload),
  deleteFlow: (id) => apiV2.delete(`/connect/flows/${id}`),
  addFlowItem: (flowId, payload) => apiV2.post(`/connect/flows/${flowId}/items`, payload),
  updateFlowItem: (flowId, itemId, payload) => apiV2.put(`/connect/flows/${flowId}/items/${itemId}`, payload),
  removeFlowItem: (flowId, itemId) => apiV2.delete(`/connect/flows/${flowId}/items/${itemId}`),
  earnings: () => apiV2.get('/connect/earnings'),
  stripeOnboard: () => apiV2.post('/connect/stripe/onboard'),
  stripeStatus: () => apiV2.get('/connect/stripe/status'),
  // Operator listing controls (org-scoped, admins)
  myListings: () => apiV2.get(`/organizations/${getOrgId()}/connect/my-listings`),
  toggleListing: (serviceId, optIn) => apiV2.put(`/organizations/${getOrgId()}/connect/my-listings`, { serviceId, optIn }),
  orgSettings: (payload) => apiV2.put(`/organizations/${getOrgId()}/connect/settings`, payload),
};

// Super Admin endpoints (for super admin dashboard)
export const superAdminApi = {
  login: (username, password) => apiV2.post('/super-admin/auth/login', { username, password }),
  getOrganizations: () => apiV2.get('/super-admin/organizations'),
  getOrganization: (id) => apiV2.get(`/super-admin/organizations/${id}`),
  approveOrganization: (id) => apiV2.post(`/super-admin/organizations/${id}/approve`),
  suspendOrganization: (id) => apiV2.post(`/super-admin/organizations/${id}/suspend`),
  activateOrganization: (id) => apiV2.post(`/super-admin/organizations/${id}/activate`),
  updateOrganization: (id, data) => apiV2.patch(`/super-admin/organizations/${id}`, data),
  adjustActions: (id, amount, reason) => apiV2.post(`/super-admin/organizations/${id}/actions`, { amount, reason }),
  getPlatformStats: () => apiV2.get('/super-admin/platform-stats'),
  getPlatformFinancials: (period = 'all') => apiV2.get(`/super-admin/platform-financials?period=${period}`),
  getActivityLogs: (limit = 100) => apiV2.get(`/super-admin/activity-logs?limit=${limit}`),
};
